import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { mergeDiscoveredLaunchPages, type DiscoveredLaunchPage, type WindsorOrganicPage, type AccountPageDiscovery, type ConfirmedLaunchPage } from "@shared/ad-launcher-account";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/lib/picker-search";

export function useOrganicLaunchPages(brandId: string, enabled: boolean) {
  return useQuery<{ pages: WindsorOrganicPage[] }>({
    queryKey: [`/api/windsor/brands/${brandId}/available-pages`],
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => (await apiRequest("GET", `/api/windsor/brands/${brandId}/available-pages`)).json(),
  });
}

export function useConfirmedLaunchPages(brandId: string, enabled: boolean) {
  return useQuery<{ pages: ConfirmedLaunchPage[] }>({
    queryKey: [`/api/windsor/brands/${brandId}/confirmed-pages`],
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => (await apiRequest("GET", `/api/windsor/brands/${brandId}/confirmed-pages`)).json(),
  });
}

export function useAccountLaunchPages(brandId: string, providerAccountId: string, savedRowId: string | undefined, enabled: boolean) {
  const params = new URLSearchParams(savedRowId ? { accountId: savedRowId } : { availableAccountId: providerAccountId });
  const url = `/api/windsor/brands/${brandId}/account-pages?${params}`;
  const [refresh, setRefresh] = useState({ url: "", generation: 0 });
  const refreshGeneration = refresh.url === url ? refresh.generation : 0;
  const query = useQuery<AccountPageDiscovery>({
    queryKey: [url, providerAccountId],
    enabled: enabled && /^\d+$/.test(providerAccountId) && refreshGeneration === 0,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => (await apiRequest("GET", url)).json(),
  });
  // Refetch has its own key, so a late refresh can only update that account's
  // cache. Query data is also checked against the selected provider account.
  const freshQuery = useQuery<AccountPageDiscovery>({
    queryKey: [url, providerAccountId, "fresh", refreshGeneration],
    enabled: enabled && /^\d+$/.test(providerAccountId) && refreshGeneration > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () => (await apiRequest("GET", `${url}&fresh=1`)).json(),
  });
  const active = refreshGeneration > 0 ? freshQuery : query;
  return {
    accountPages: active.data?.accountId === providerAccountId ? active.data.pages : [],
    accountLoading: active.isLoading,
    accountError: active.isError,
    accountFetching: active.isFetching,
    accountSelected: !!providerAccountId,
    onRetryAccount: () => setRefresh(previous => ({ url, generation: previous.url === url ? previous.generation + 1 : 1 })),
  };
}

export type LaunchPageDiscoveryProps = {
  metaPages?: Array<{ id?: string; name?: string }>;
  metaLoading?: boolean;
  metaError?: boolean;
  metaFetching?: boolean;
  onRetryMeta?: () => void;
  organicPages?: WindsorOrganicPage[];
  confirmedPages?: ConfirmedLaunchPage[];
  confirmedLoading?: boolean;
  confirmedError?: boolean;
  confirmedFetching?: boolean;
  onRetryConfirmed?: () => void;
  loading: boolean;
  error: boolean;
  fetching: boolean;
  onRetry: () => void;
  accountPages?: AccountPageDiscovery["pages"];
  accountLoading?: boolean;
  accountError?: boolean;
  accountFetching?: boolean;
  accountSelected?: boolean;
  onRetryAccount?: () => void;
};

/** No effects write form state. A user choice is required before fields change. */
export function LaunchPageDiscovery({
  metaPages = [], organicPages = [], loading, error, fetching, onRetry,
  confirmedPages = [], confirmedLoading, confirmedError, confirmedFetching, onRetryConfirmed,
  metaLoading, metaError, metaFetching, onRetryMeta,
  accountPages = [], accountLoading, accountError, accountFetching, accountSelected, onRetryAccount,
  onSelect, excludedIds = [], testId, immediate = false, value, label = "Connected Facebook Pages",
}: LaunchPageDiscoveryProps & {
  onSelect: (page: DiscoveredLaunchPage) => void;
  excludedIds?: string[];
  testId: string;
  immediate?: boolean;
  value?: string;
  label?: string;
}) {
  const [selectedId, setSelectedId] = useState("");
  const choices = mergeDiscoveredLaunchPages(metaPages, organicPages, accountPages, confirmedPages)
    .filter(page => !excludedIds.includes(page.pageId));
  const selected = choices.find(page => page.pageId === selectedId);
  const current = immediate ? value : selected?.pageId;
  const options = choices.map(page => ({
    value: page.pageId,
    label: `${page.name} · ${page.pageId} · ${page.sources.map(source => source === "account_history" ? "Observed on this ad account" : source === "meta_ads" ? "Meta Ads" : source === "business_settings" ? "Business Settings–confirmed" : "Windsor Organic").join(" + ")}`,
  }));
  // A saved/detected/manual value is display-only; discovery never clears it.
  if (current && !options.some(option => option.value === current)) {
    options.push({ value: current, label: `Page ${current} · entered or detected` });
  }
  return (
    <div className="min-w-0 space-y-2 rounded-md border p-2" data-testid={testId}>
      <p className="text-xs font-medium">{label}</p>
      {metaLoading && <p role="status" className="text-xs text-muted-foreground">Loading connected Meta Pages…</p>}
      {metaError && <p role="alert" className="text-xs text-destructive">Connected Meta Pages could not be loaded. Other sources and manual values remain available.</p>}
      {accountLoading && accountSelected && <p role="status" className="text-xs text-muted-foreground">Loading this ad account’s observed advertising Pages…</p>}
      {accountError && accountSelected && <p role="alert" className="text-xs text-destructive">Advertising Page history could not establish Pages for this account. Retry or enter a Page ID manually; connected and saved Pages are unchanged.</p>}
      {accountSelected && !accountLoading && !accountError && accountPages.length === 0 && <p className="text-xs text-muted-foreground">No Page identities found in this account’s recent advertising history. This is not a complete inventory; other Page sources below remain selectable, or enter a Page ID manually.</p>}
      {confirmedLoading && <p role="status" className="text-xs text-muted-foreground">Loading Business Settings–confirmed Pages…</p>}
      {confirmedError && <p role="alert" className="text-xs text-destructive">Business Settings–confirmed Pages could not be loaded. Other sources and manual values remain available.</p>}
      {loading && <p role="status" className="text-xs text-muted-foreground">Loading Windsor Organic Pages…</p>}
      {error && <p role="alert" className="text-xs text-destructive">Windsor Organic Pages could not be loaded. Saved and manual values are unchanged.</p>}
      {!loading && !error && organicPages.length === 0 && <p className="text-xs text-muted-foreground">No connected Windsor Organic Pages found.</p>}
      <SearchableSelect
        value={current || "__none__"}
        onValueChange={(id) => {
          if (immediate) {
            const page = choices.find(page => page.pageId === id);
            if (page) onSelect(page);
          } else setSelectedId(id);
        }}
        testId={`${testId}-select`}
        ariaLabel="Discovered Facebook Page"
        options={[
          { value: "__none__", label: "Choose a Facebook Page" },
          ...options,
        ]}
      />
      <Button type="button" size="sm" variant="ghost" onClick={onRetry} disabled={fetching}>{fetching ? "Loading Pages…" : "Reload Organic Pages"}</Button>
      {onRetryMeta && <Button type="button" size="sm" variant="ghost" onClick={onRetryMeta} disabled={metaFetching}>Reload Meta Pages</Button>}
      {onRetryConfirmed && <Button type="button" size="sm" variant="ghost" onClick={onRetryConfirmed} disabled={confirmedFetching}>Reload confirmed Pages</Button>}
      {accountSelected && onRetryAccount && <Button type="button" size="sm" variant="ghost" onClick={onRetryAccount} disabled={accountFetching}>{accountFetching ? "Loading account Pages…" : "Reload account Pages"}</Button>}
      {!immediate && <Button type="button" size="sm" variant="outline" disabled={!selected} data-testid={`${testId}-apply`} onClick={() => {
        if (selected) onSelect(selected);
        setSelectedId("");
      }}>Use selected Page</Button>}
      <p className="text-[11px] text-muted-foreground">
        Historical use does not prove current permission or list every Page. Business Settings–confirmed choices come from operator-provided Page evidence, not a live permissions check. Meta must allow this ad account to advertise as the selected Page. Review and Save to configure it.
      </p>
    </div>
  );
}
